import { useCallback, useEffect, useRef } from 'react';

const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'click',
];

const DEFAULT_THROTTLE_MS = 2_000;

export interface UseInactivityTimerResult {
  getIdleMs: () => number;
  markActive: () => void;
}

/**
 * Tracks how long the user has been idle in the current tab.
 *
 * Listens for mouse, keyboard, scroll, and touch events (throttled so we
 * don't spin the reconciler). The `visibilitychange` handler treats a
 * returning-foreground tab as fresh activity so users who come back from
 * another tab are not immediately flagged as idle.
 *
 * Not reactive — consumers must poll `getIdleMs()` from a timer. This is
 * intentional: we don't want every mouse move to rerender the provider.
 */
export const useInactivityTimer = (
  throttleMs: number = DEFAULT_THROTTLE_MS
): UseInactivityTimerResult => {
  const lastActivityRef = useRef<number>(Date.now());
  const lastUpdateRef = useRef<number>(0);

  const markActive = useCallback(() => {
    lastActivityRef.current = Date.now();
    lastUpdateRef.current = Date.now();
  }, []);

  useEffect(() => {
    const handleActivity = () => {
      const now = Date.now();

      if (now - lastUpdateRef.current < throttleMs) return;
      lastUpdateRef.current = now;
      lastActivityRef.current = now;
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        markActive();
      }
    };

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [throttleMs, markActive]);

  const getIdleMs = useCallback(() => Date.now() - lastActivityRef.current, []);

  return { getIdleMs, markActive };
};
